/**
 * Landing FAQ. Both the visible Q&A and the FAQPage JSON-LD are built from the
 * same published `faq` items tagged "home", so they can't drift apart. The
 * JSON-LD is rendered into <head> at runtime (crawlers that execute JS, e.g.
 * Googlebot, read it there).
 */
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { renderRich } from "@/lib/richText";

// Strip **bold** / [label](url) markup to plain text for the JSON-LD "text".
const toPlain = (s) =>
  String(s || "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");

const FaqSection = () => {
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/public/faq?page=home`)
      .then((r) => { if (mounted) setFaqs(r.data?.items || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Keep the FAQPage JSON-LD in <head> in sync with the rendered items.
  useEffect(() => {
    const id = "faq-jsonld";
    document.getElementById(id)?.remove();
    if (!faqs.length) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: toPlain(f.answer) },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => { document.getElementById(id)?.remove(); };
  }, [faqs]);

  if (!faqs.length) return null;

  return (
    <section id="faq" className="py-24 px-6 border-t border-border" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Frequently asked questions</h2>
        <p className="text-muted-foreground text-center mb-10 text-sm">
          Everything candidates ask before starting.
        </p>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={f.id || i} className="group rounded-2xl border border-border bg-card px-6 py-4" data-testid={`faq-item-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer list-none font-medium text-sm md:text-base [&::-webkit-details-marker]:hidden">
                {f.question}
                <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-180 flex-shrink-0 ml-3" />
              </summary>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{renderRich(f.answer, "text-foreground")}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
